import { useEffect, useMemo } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { DataSet } from "vis-data";
import { TimelineGroup, TimelineItem } from "vis-timeline/standalone";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";

import { unwrappableActionSnackbarNotificationDecorator } from "utils/unwrappableActionSnackbarNotificationDecorator";
import { caseViewerActions, caseViewerThunks } from "./store/slice";
import { playbackSelectors } from "./store/selectors.playback";
import { FetchPersistedRecordingsForDossierDeviceRejectedReason } from "./types/FetchPersistedRecordingsForDossierDeviceRejectedReason";
import { caseViewerSelectors } from "./store/selectors";

export interface PlaybackTimelineGroupProps {
    timelineGroups: React.MutableRefObject<DataSet<TimelineGroup, "id">>;
    timelineItems: React.MutableRefObject<DataSet<TimelineItem, "id">>;
    dossierDeviceId: EntityId;
    startDateTime: string;
    endDateTime: string;
}

export function PlaybackTimelineGroup(props: PlaybackTimelineGroupProps) {
    const dispatch = useAppDispatch();

    const dossierDevice = useAppSelector((state: RootState) =>
        caseViewerSelectors.selectSelectedDossierDeviceById(
            state,
            props.dossierDeviceId,
        ),
    );

    // fetch recordings for this device
    useEffect(() => {
        const fetchPersistedRecordingsForDevice = dispatch(
            caseViewerThunks.fetchPersistedRecordingsForDossierDeviceAsync({
                dossierDeviceId: props.dossierDeviceId,
            }),
        );

        unwrappableActionSnackbarNotificationDecorator(
            fetchPersistedRecordingsForDevice,
            undefined,
            (
                reason: FetchPersistedRecordingsForDossierDeviceRejectedReason,
            ) => {
                // deselect device
                dispatch(
                    caseViewerActions.upsertVisibleDossierDeviceIdState({
                        dossierDeviceId: props.dossierDeviceId,
                        visible: false,
                    }),
                );

                return reason.message;
            },
        );

        return () => fetchPersistedRecordingsForDevice?.abort();
    }, [
        dispatch,
        props.dossierDeviceId,
        props.endDateTime,
        props.startDateTime,
    ]);

    // get persisted recordings for this device from store
    const selectPersistedRecordingsByDossierDeviceId = useMemo(
        () =>
            playbackSelectors.selectPersistedRecordingsByDossierDeviceIdFactory(),
        [],
    );

    const devicePersistedRecordings = useAppSelector((state: RootState) =>
        selectPersistedRecordingsByDossierDeviceId(
            state,
            props.dossierDeviceId,
        ),
    );

    // draw recordings on timeline
    useEffect(() => {
        if (
            !props.timelineGroups.current ||
            !props.timelineItems.current ||
            !dossierDevice?.name
        )
            return;

        // upsert group
        const groupId = props.dossierDeviceId.toString();
        const existingGroup = props.timelineGroups.current.get(groupId);

        if (!existingGroup) {
            props.timelineGroups.current.add({
                id: groupId,
                content: dossierDevice.name,
            });
        }

        // upsert recordings
        props.timelineItems.current.remove(
            devicePersistedRecordings.map((x) => x.id),
        );
        const items = devicePersistedRecordings.map<TimelineItem>(
            (persistedRecording) => ({
                id: persistedRecording.id,
                group: groupId,
                start: persistedRecording.persistedStartTime,
                end: persistedRecording.persistedStopTime,
                type: "range",
                content: "",
                title: `${persistedRecording.id.toString()} - ${persistedRecording.status}`,
                editable: false,
                className: `status-${persistedRecording.status}`,
            }),
        );

        props.timelineItems.current.add(items);

        // remove group and items when user deselects device
        return () => {
            props.timelineGroups.current?.remove(groupId);
            props.timelineItems.current?.remove(
                devicePersistedRecordings.map((x) => x.id),
            );
        };
    }, [
        devicePersistedRecordings,
        props.timelineGroups,
        props.timelineItems,
        dossierDevice,
    ]);

    // in the future create a React based timeline
    return <></>;
}
